import {Asset, AssetTypeFilter, Tag} from '../../../types';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {getAxiosError, useFormSubmit} from '@alchemy/api';
import FormTab from '../Tabbed/FormTab';
import {DialogTabProps} from '../Tabbed/TabbedDialog';
import {
    AssetApiInput,
    attributeBatchUpdate,
    putAsset,
} from '../../../api/asset';
import {Privacy} from '../../../api/privacy';
import {FormFieldErrors, FormRow} from '@alchemy/react-form';
import {FormGroup, FormLabel, InputLabel, Skeleton} from '@mui/material';
import TagSelect from '../../Form/TagSelect';
import PrivacyField from '../../Ui/PrivacyField';
import {useAssetStore} from '../../../store/assetStore.ts';
import {useDirtyFormPrompt} from '@alchemy/phrasea-framework';
import {useAttributeEditor} from '../../Media/Asset/Attribute/useAttributeEditor.ts';
import React from 'react';
import AttributesEditor from '../../Media/Asset/Attribute/AttributesEditor.tsx';

type Props = {
    id: string;
    data: Asset;
} & DialogTabProps;

export default function EditAsset({data, onClose, minHeight}: Props) {
    const {t} = useTranslation();
    const assetTypeFilter = data.storyCollection
        ? AssetTypeFilter.Story
        : AssetTypeFilter.Asset;

    const formId = 'edit-asset';
    const updateAsset = useAssetStore(s => s.update);

    const [error, setError] = React.useState<string | undefined>();

    const {
        getActions,
        onChangeHandler,
        attributes,
        definitionIndex,
        reloadAssetAttributes,
        dirty: attributesDirty,
    } = useAttributeEditor({
        workspaceId: data.workspace.id,
        assetId: data.id,
        target: assetTypeFilter,
    });

    const saveAttributes = React.useCallback(async () => {
        const actions = getActions();
        try {
            if (actions.length > 0) {
                await attributeBatchUpdate(data.id, actions);
            }
            await reloadAssetAttributes(data.id);

            if (error) {
                setError(undefined);
            }
        } catch (e: any) {
            // eslint-disable-next-line no-console
            console.error('e', e);
            const error = getAxiosError(e);
            setError(error?.message ?? e.toString());

            throw e;
        }
    }, [getActions]);

    const {
        control,
        formState: {errors, isDirty: editDirty},
        submitting,
        handleSubmit,
        remoteErrors,
        forbidNavigation,
    } = useFormSubmit<Asset>({
        defaultValues: data
            ? {
                  name: data.name,
                  privacy: data.privacy,
                  tags: (data?.tags?.map(t => t['@id']) ??
                      []) as unknown as Tag[],
              }
            : {
                  name: '',
                  privacy: Privacy.Secret,
                  tags: [] as Tag[],
              },
        onSubmit: async d => {
            if (attributesDirty) {
                await saveAttributes();
            }

            if (editDirty) {
                const asset = await putAsset(
                    data.id,
                    d as unknown as AssetApiInput
                );
                updateAsset(asset);
                return asset;
            }

            return data;
        },
        onSuccess: () => {
            toast.success(
                t('form.asset_edit.success', 'Asset edited!') as string
            );
            onClose();
        },
    });

    useDirtyFormPrompt(!submitting && (attributesDirty || forbidNavigation));

    const {capabilities} = data;

    return (
        <FormTab
            onClose={onClose}
            formId={formId}
            loading={submitting}
            errors={remoteErrors}
            minHeight={minHeight}
        >
            <form id={formId} onSubmit={handleSubmit}>
                {capabilities.edit ? (
                    <>
                        <FormRow>
                            <FormGroup>
                                <InputLabel>
                                    {t('form.asset.tags.label', 'Tags')}
                                </InputLabel>
                                <TagSelect
                                    multiple={true}
                                    workspaceId={data.workspace.id}
                                    control={control}
                                    name={'tags'}
                                />
                                <FormFieldErrors<Asset>
                                    field={'tags'}
                                    errors={errors}
                                />
                            </FormGroup>
                        </FormRow>
                        {capabilities.editPermissions ? (
                            <FormRow>
                                <PrivacyField
                                    control={control}
                                    name={'privacy'}
                                />
                            </FormRow>
                        ) : null}
                    </>
                ) : null}
            </form>
            {capabilities.editAttributes ? (
                attributes && definitionIndex ? (
                    <AttributesEditor
                        attributes={attributes}
                        definitions={definitionIndex}
                        disabled={submitting}
                        onChangeHandler={onChangeHandler}
                        assetTypeFilter={assetTypeFilter}
                    />
                ) : (
                    <>
                        {[0, 1, 2].map(x => (
                            <React.Fragment key={x}>
                                <FormRow>
                                    <FormLabel>
                                        <Skeleton
                                            width={'200'}
                                            variant={'text'}
                                            style={{
                                                display: 'inline-block',
                                                width: '200px',
                                            }}
                                        />
                                    </FormLabel>
                                    <Skeleton
                                        width={'100%'}
                                        height={56}
                                        variant={'rectangular'}
                                        sx={{
                                            mb: 2,
                                        }}
                                    />
                                </FormRow>
                            </React.Fragment>
                        ))}
                    </>
                )
            ) : null}
        </FormTab>
    );
}
